import Action  from './baseActions';
import {IBlock} from '../../models';

export enum BlockActionTypes {
  UNSET_BLOCKS = 'block/unset_blocks',

  LOADED_BLOCKS = 'block/loaded_blocks',

  CREATE_BLOCK = 'block/create_block',
  CREATING_BLOCK = 'block/creating_block',
  CREATED_BLOCK = 'block/created_block',
  CREATING_BLOCK_FAILED = 'block/creating_block_failed',

  CREATE_BLOCKS = 'block/create_blocks',
  CREATING_BLOCKS = 'block/creating_blocks',
  CREATED_BLOCKS = 'block/created_blocks',
  CREATING_BLOCKS_FAILED = 'block/creating_blocks_failed',

  UPDATE_BLOCK = 'block/update_block',
  UPDATING_BLOCK = 'block/updating_block',
  UPDATED_BLOCK = 'block/updated_block',
  UPDATING_BLOCK_FAILED = 'block/updating_block_failed',

  UPDATE_BLOCKS = 'block/update_blocks',
  UPDATING_BLOCKS = 'block/updating_blocks',
  UPDATED_BLOCKS = 'block/updated_blocks',
  UPDATING_BLOCKS_FAILED = 'block/updating_blocks_failed',

  DELETE_BLOCK = 'block/delete_block',
  DELETING_BLOCK = 'block/deleting_block',
  DELETED_BLOCK = 'block/deleted_block',
  DELETING_BLOCK_FAILED = 'block/deleting_block_failed',

  LOAD_PREVIEW = 'block/load_preview',
  LOADING_PREVIEW = 'block/loading_preview',
  LOADED_PREVIEW = 'block/loaded_preview',
  LOADING_PREVIEW_FAILED = 'block/loading_preview_failed',
  UNSET_PREVIEW = 'block/unset_preview',
}

// Interfaces

export interface UnsetBlocksAction extends Action {
  type: BlockActionTypes.UNSET_BLOCKS;
}

export interface LoadedBlocksAction extends Action {
  type: BlockActionTypes.LOADED_BLOCKS;
  payload: IBlock[];
}

export interface CreateBlockAction extends Action {
  type: BlockActionTypes.CREATE_BLOCK;
  payload: Partial<IBlock>;
}

export interface CreatingBlockAction extends Action {
  type: BlockActionTypes.CREATING_BLOCK;
}

export interface CreatedBlockAction extends Action {
  type: BlockActionTypes.CREATED_BLOCK;
  payload: any;
}

export interface CreatingBlockFailedAction extends Action {
  type: BlockActionTypes.CREATING_BLOCK_FAILED;
}

export interface CreateBlocksAction extends Action {
  type: BlockActionTypes.CREATE_BLOCKS;
  payload: Partial<IBlock>[];
}

export interface CreatingBlocksAction extends Action {
  type: BlockActionTypes.CREATING_BLOCKS;
}

export interface CreatedBlocksAction extends Action {
  type: BlockActionTypes.CREATED_BLOCKS;
  payload: any;
}

export interface CreatingBlocksFailedAction extends Action {
  type: BlockActionTypes.CREATING_BLOCKS_FAILED;
}

export interface UpdateBlockAction extends Action {
  type: BlockActionTypes.UPDATE_BLOCK;
  payload: {
    uid: string;
    block: Partial<IBlock>;
  };
}

export interface UpdatingBlockAction extends Action {
  type: BlockActionTypes.UPDATING_BLOCK;
}

export interface UpdatedBlockAction extends Action {
  type: BlockActionTypes.UPDATED_BLOCK;
  payload: {
    block: any,
    fields: string[] | null, // updated fields
  };
}

export interface UpdatingBlockFailedAction extends Action {
  type: BlockActionTypes.UPDATING_BLOCK_FAILED;
}

export interface UpdateBlocksAction extends Action {
  type: BlockActionTypes.UPDATE_BLOCKS;
  payload: {
    blocks: Partial<IBlock>[];
  };
}

export interface UpdatingBlocksAction extends Action {
  type: BlockActionTypes.UPDATING_BLOCKS;
}

export interface UpdatedBlocksAction extends Action {
  type: BlockActionTypes.UPDATED_BLOCKS;
  payload: {
    blocks: any[];
  };
}

export interface UpdatingBlocksFailedAction extends Action {
  type: BlockActionTypes.UPDATING_BLOCKS_FAILED;
}

export interface DeleteBlockAction extends Action {
  type: BlockActionTypes.DELETE_BLOCK;
  payload: {
    uid: string;
    softDelete: boolean; // delete only from redux, but not from the BE
  };
}

export interface DeletingBlockAction extends Action {
  type: BlockActionTypes.DELETING_BLOCK;
}

export interface DeletedBlockAction extends Action {
  type: BlockActionTypes.DELETED_BLOCK;
  payload: string;
}

export interface DeletingBlockFailedAction extends Action {
  type: BlockActionTypes.DELETING_BLOCK_FAILED;
}

export interface LoadPreviewAction extends Action {
  type: BlockActionTypes.LOAD_PREVIEW;
  payload: {
    uid: string;
    reportUid: string;
    block: Partial<IBlock>;
  };
}

export interface LoadingPreviewAction extends Action {
  type: BlockActionTypes.LOADING_PREVIEW;
}

export interface LoadedPreviewAction extends Action {
  type: BlockActionTypes.LOADED_PREVIEW;
  payload: {
    uid: string;
    result: any;
  };
}

export interface LoadingPreviewFailedAction extends Action {
  type: BlockActionTypes.LOADING_PREVIEW_FAILED;
}

export interface UnsetPreviewAction extends Action {
  type: BlockActionTypes.UNSET_PREVIEW;
  payload: string;
}

// Functions

export function unsetBlocks(): UnsetBlocksAction {
  return {
    type: BlockActionTypes.UNSET_BLOCKS,
  };
}

export function loadedBlocks(blocks: IBlock[]): LoadedBlocksAction {
  return {
    type: BlockActionTypes.LOADED_BLOCKS,
    payload: blocks,
  };
}

export function createBlock(block: Partial<IBlock>): CreateBlockAction {
  return {
    type: BlockActionTypes.CREATE_BLOCK,
    payload: block,
  };
}

export function creatingBlock(): CreatingBlockAction {
  return {
    type: BlockActionTypes.CREATING_BLOCK,
  };
}

export function createdBlock(block: any): CreatedBlockAction {
  return {
    type: BlockActionTypes.CREATED_BLOCK,
    payload: block,
  };
}

export function creatingBlockFailed(): CreatingBlockFailedAction {
  return {
    type: BlockActionTypes.CREATING_BLOCK_FAILED,
  };
}

export function createBlocks(blocks: Partial<IBlock>[]): CreateBlocksAction {
  return {
    type: BlockActionTypes.CREATE_BLOCKS,
    payload: blocks,
  };
}

export function creatingBlocks(): CreatingBlocksAction {
  return {
    type: BlockActionTypes.CREATING_BLOCKS,
  };
}

export function createdBlocks(blocks: any): CreatedBlocksAction {
  return {
    type: BlockActionTypes.CREATED_BLOCKS,
    payload: blocks,
  };
}

export function creatingBlocksFailed(): CreatingBlocksFailedAction {
  return {
    type: BlockActionTypes.CREATING_BLOCKS_FAILED,
  };
}

export function updateBlock(uid: string, block: Partial<IBlock>): UpdateBlockAction {
  return {
    type: BlockActionTypes.UPDATE_BLOCK,
    payload: {
      uid,
      block,
    },
  };
}

export function updatingBlock(): UpdatingBlockAction {
  return {
    type: BlockActionTypes.UPDATING_BLOCK,
  };
}

export function updatedBlock(block: any, fields: string[] | null = null): UpdatedBlockAction {
  /*
   * `fields` represent the fields that were updated; can be omitted.
   */
  return {
    type: BlockActionTypes.UPDATED_BLOCK,
    payload: {
      block,
      fields: fields,
    }
  };
}

export function updatingBlockFailed(): UpdatingBlockFailedAction {
  return {
    type: BlockActionTypes.UPDATING_BLOCK_FAILED,
  };
}

export function updateBlocks(blocks: Partial<IBlock>[]): UpdateBlocksAction {
  return {
    type: BlockActionTypes.UPDATE_BLOCKS,
    payload: {
      blocks,
    },
  };
}

export function updatingBlocks(): UpdatingBlocksAction {
  return {
    type: BlockActionTypes.UPDATING_BLOCKS,
  };
}

export function updatedBlocks(blocks: any[]): UpdatedBlocksAction {
  return {
    type: BlockActionTypes.UPDATED_BLOCKS,
    payload: {
      blocks,
    },
  };
}

export function updatingBlocksFailed(): UpdatingBlocksFailedAction {
  return {
    type: BlockActionTypes.UPDATING_BLOCKS_FAILED,
  };
}

export function deleteBlock(uid: string, softDelete: boolean = false): DeleteBlockAction {
  return {
    type: BlockActionTypes.DELETE_BLOCK,
    payload: {
      uid,
      softDelete,
    },
  };
}

export function deletingBlock(): DeletingBlockAction {
  return {
    type: BlockActionTypes.DELETING_BLOCK,
  };
}

export function deletedBlock(uid: string): DeletedBlockAction {
  return {
    type: BlockActionTypes.DELETED_BLOCK,
    payload: uid,
  };
}

export function deletingBlockFailed(): DeletingBlockFailedAction {
  return {
    type: BlockActionTypes.DELETING_BLOCK_FAILED,
  };
}

export function loadPreview(uid: string, reportUid: string, block: Partial<IBlock>): LoadPreviewAction {
  return {
    type: BlockActionTypes.LOAD_PREVIEW,
    payload: {
      uid,
      reportUid,
      block,
    }
  };
}

export function loadingPreview(): LoadingPreviewAction {
  return {
    type: BlockActionTypes.LOADING_PREVIEW,
  };
}

export function loadedPreview(uid: string, result: any): LoadedPreviewAction {
  return {
    type: BlockActionTypes.LOADED_PREVIEW,
    payload: {
      uid,
      result,
    },
  };
}

export function loadingPreviewFailed(): LoadingPreviewFailedAction {
  return {
    type: BlockActionTypes.LOADING_PREVIEW_FAILED,
  };
}

export function unsetPreview(uid: string): UnsetPreviewAction {
  return {
    type: BlockActionTypes.UNSET_PREVIEW,
    payload: uid,
  }
}

// Types

export type BlockActions =
  | UnsetBlocksAction
  | LoadedBlocksAction
  | CreateBlockAction
  | CreatingBlockAction
  | CreatedBlockAction
  | CreatingBlockFailedAction
  | CreateBlocksAction
  | CreatingBlocksAction
  | CreatedBlocksAction
  | CreatingBlocksFailedAction
  | UpdateBlockAction
  | UpdatingBlockAction
  | UpdatedBlockAction
  | UpdatingBlockFailedAction
  | UpdateBlocksAction
  | UpdatingBlocksAction
  | UpdatedBlocksAction
  | UpdatingBlocksFailedAction
  | DeleteBlockAction
  | DeletingBlockAction
  | DeletedBlockAction
  | DeletingBlockFailedAction
  | LoadPreviewAction
  | LoadingPreviewAction
  | LoadedPreviewAction
  | LoadingPreviewFailedAction
  | UnsetPreviewAction;
